import { useEffect, useMemo, useState } from "react";
import CompareChoicePage from "./components/CompareChoicePage";
import { NotificationsBell } from "./components/NotificationsBell";
import axios from "axios";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { createWorker } from "tesseract.js";

// Configure PDF.js worker (uses matching CDN version, no bundler config needed)
if (typeof window !== "undefined" && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://unpkg.com/pdfjs-dist@4.10.38/legacy/build/pdf.worker.min.mjs";
}

import {
  LayoutDashboard, FileText, Users, RefreshCw, WalletCards, BarChart3, Search, Bell, ChevronDown, Menu, X, Trash2, Building2, Settings as SettingsIcon, LogOut, ExternalLink, Mail, Shield, Megaphone, KeyRound, Car, CalendarClock, UserPlus,
} from "lucide-react";
import { Appointments } from "./components/Appointments";
import { Leads } from "./components/Leads";
import { AppointmentModal } from "./components/AppointmentModal";
import { LeadModal } from "./components/LeadModal";
import { QuickAddFab } from "./components/QuickAddFab";
import { useAgenda } from "./lib/useAgenda";
import { QuoteCheckModal, PendingChoices } from "./components/QuoteCheckModal";
import { daysLeft, calcCommission, branchRateOf, effectiveRate } from "./lib/finance";
import { parsePolicyText } from "./lib/pdfTemplates";
import { useAuth } from "./lib/auth";
import AdminPanel from "./AdminPanel";
import AuthPage from "./AuthPage";
import ResetPasswordPage from "./ResetPasswordPage";
import { Vehicles } from "./components/Vehicles";
import { PolicyModal } from "./components/PolicyModal";
import { QuoteModal } from "./components/QuoteModal";
import { Reports } from "./components/Reports";
import { Dashboard } from "./components/Dashboard";
import { Policies } from "./components/Policies";
import { Customers } from "./components/Customers";
import { Renewals } from "./components/Renewals";
import { Commission } from "./components/Commission";
import { QuotesPage } from "./components/Quotes";
import { PageErrorBoundary } from "./components/PageErrorBoundary";
import { Branches } from "./components/Branches";
import { TrashPage } from "./components/Trash";
import { Toaster } from "sonner";
import { undoToast, restoreFromTrash } from "./lib/undo";
import { CustomerModal, CustomerDetail, CustomerPortalLinkModal } from "./components/CustomerModals";
import { PdfImportModal, ExcelImportModal } from "./components/ImportModals";
import { ChangePasswordModal, ChangeEmailModal } from "./components/AccountModals";
import { ThemeToggle, PdfPreviewModal, InstallPrompt } from "./components/AppChrome";
import { VehicleModal } from "./components/VehicleModal";
import { FollowUpModal, SettingsModal } from "./components/MiscModals";
import { ConvertQuoteModal, OnlineQuoteLinkModal, QuoteEmailModal } from "./components/QuoteModals";
import { PublicQuoteForm } from "./components/PublicQuoteForm";
import { GlobalSearchModal } from "./components/GlobalSearchModal";
import { CustomerPortal } from "./components/CustomerPortal";
import { Empty } from "./components/common";
import { SettingsPage, CalcSettingsCard } from "./components/SettingsPage";
import { findRenewedIds, isLost } from "./lib/renewal";
import { UserAvatar } from "./components/UserAvatar";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YEAR = new Date().getFullYear();
const MONTHS_FULL = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function App() {
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const [page, setPage] = useState(() => new URLSearchParams(window.location.search).get("page") || "dashboard");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("page")) {
      params.delete("page");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => { if (e.data?.type === "navigate" && e.data.page) setPage(e.data.page); };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);
  const [policies, setPolicies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteFilter, setQuoteFilter] = useState(() => new URLSearchParams(window.location.search).get("filter") || "all");
  const [editingAppt, setEditingAppt] = useState(null);
  const [checkQuoteId, setCheckQuoteId] = useState(() => new URLSearchParams(window.location.search).get("check"));
  const [editingLead, setEditingLead] = useState(null);
  const { leads, setLeads, appointments, setAppointments, saveLead, convertLead, deleteLead, upsertAppt } = useAgenda(user?.id, setCustomers, () => { setModal(null); setEditingLead(null); });
  const [trashPolicies, setTrashPolicies] = useState([]);
  const [editingQuote, setEditingQuote] = useState(null);
  const [publicQuoteSlug, setPublicQuoteSlug] = useState("");
  const [onlineLinkOpen, setOnlineLinkOpen] = useState(false);
  const [convertingQuote, setConvertingQuote] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [emailingQuote, setEmailingQuote] = useState(null);
  const [portalCustomer, setPortalCustomer] = useState(null);
  const [settings, setSettings] = useState({ netDivisor: 1.12, personalShare: 60 });
  const [modal, setModal] = useState(null);
  const [query, setQuery] = useState("");
  const [apiStatus, setApiStatus] = useState("checking");
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [presetPolicyCustomer, setPresetPolicyCustomer] = useState("");
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [pdfImport, setPdfImport] = useState(false);
  const [followUpPolicy, setFollowUpPolicy] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [pendingPdfFile, setPendingPdfFile] = useState(null);
  const [excelImport, setExcelImport] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const [pol, cus, br, fu, st, veh, qt] = await Promise.all([
          axios.get(`${API}/policies`),
          axios.get(`${API}/customers`),
          axios.get(`${API}/branches`),
          axios.get(`${API}/followups`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/vehicles`).catch(() => ({ data: [] })),
          axios.get(`${API}/quotes`).catch(() => ({ data: [] })),
        ]);
        setPolicies(pol.data);
        setCustomers(cus.data);
        setBranches(br.data);
        setFollowUps(fu.data);
        setSettings(st.data);
        setVehicles(veh.data || []);
        setQuotes(qt.data || []);
        setApiStatus("online");
      } catch (e) {
        console.error("Sunucu erişilemedi", e);
        setApiStatus("offline");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Auto-load trash whenever user opens the Çöp Kutusu page
  useEffect(() => {
    if (user && page === "trash") {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/policies/trash`);
          setTrashPolicies(data || []);
        } catch { setTrashPolicies([]); }
      })();
    }
  }, [user, page]);

  // Global shortcut: Cmd+K / Ctrl+K opens quick search
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      } else if (e.key === "Escape" && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, searchOpen]);

  const totalPremium = useMemo(() => policies.reduce((s, p) => s + Number(p.premium || 0), 0), [policies]);
  const totalCommissionPersonal = useMemo(
    () => policies.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0),
    [policies, branches, settings]
  );
  const totalCommissionAgency = useMemo(
    () => policies.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).agency, 0),
    [policies, branches, settings]
  );
  const upcoming = useMemo(
    () => {
      const renewed = findRenewedIds(policies);
      return policies.filter((p) => p.end && !renewed.has(p.id) && !isLost(p) && daysLeft(p.end) >= 0 && daysLeft(p.end) <= 60).sort((a, b) => new Date(a.end) - new Date(b.end));
    },
    [policies]
  );

  const perms = user?.permissions || {};
  const isAdmin = user?.role === "admin";
  const can = (name) => isAdmin || !!perms[name];

  const nav = [
    ["dashboard", "Genel Bakış", LayoutDashboard, true],
    ["policies", "Poliçeler", FileText, true],
    ["quotes", "Teklifler", Megaphone, true],
    ["customers", "Müşteriler", Users, true],
    ["leads", "Potansiyel Adaylar", UserPlus, true],
    ["appointments", "Randevular", CalendarClock, true],
    ["vehicles", "Araçlar", Car, true],
    ["renewals", "Yenilemeler", RefreshCw, true],
    ["commission", "Prim & Hak Ediş", WalletCards, true],
    ["reports", "Yıllık Üretim", BarChart3, can("canViewReports")],
    ["preferences", "Ayarlar", SettingsIcon, true],
    ["trash", "Çöp Kutusu", Trash2, true],
    ["admin", "Yönetim", Shield, isAdmin],
  ].filter((row) => row[3]);

  const navigate = (nextPage) => {
    setQuoteFilter("all");
    setPage(nextPage);
    document.body.classList.remove("menu-open");
  };

  // --- CRUD handlers ---
  const savePolicy = async (item) => {
    const rawNet = item.netPremium;
    const parsedNet = (rawNet === "" || rawNet === null || rawNet === undefined) ? null : (Number(rawNet) > 0 ? Number(rawNet) : null);
    const rawRefund = item.cancellationRefund;
    const parsedRefund = (rawRefund === "" || rawRefund === null || rawRefund === undefined) ? null : (Number(rawRefund) >= 0 ? Number(rawRefund) : null);
    const normalized = {
      ...item,
      premium: Number(item.premium) || 0,
      netPremium: parsedNet,
      commissionRate: Number(item.commissionRate) || 0,
      status: item.status || "aktif",
      cancellationRefund: parsedRefund,
      cancellationDate: item.cancellationDate || "",
    };
    try {
      // Auto-create customer if the entered name doesn't exist yet (case-insensitive match)
      const trimmedCustomer = (normalized.customer || "").trim();
      const existsAlready = trimmedCustomer && customers.some(
        (c) => c.name.toLocaleLowerCase("tr") === trimmedCustomer.toLocaleLowerCase("tr")
      );
      if (trimmedCustomer && !existsAlready) {
        try {
          const { data: newCustomer } = await axios.post(`${API}/customers`, {
            name: trimmedCustomer, type: "Bireysel", phone: "", email: "", taxNo: "", address: "", note: "PDF'den otomatik oluşturuldu",
          });
          setCustomers((x) => [newCustomer, ...x]);
        } catch (_e) { /* if it fails, continue anyway */ }
      }
      let savedPolicy;
      if (item.id) {
        const { data } = await axios.put(`${API}/policies/${item.id}`, normalized);
        savedPolicy = data;
        setPolicies((x) => x.map((p) => (p.id === item.id ? data : p)));
      } else {
        const { id: _drop, ...body } = normalized;
        const { data } = await axios.post(`${API}/policies`, body);
        savedPolicy = data;
        setPolicies((x) => [data, ...x]);
      }
      // If a PDF file was queued (from PDF-import flow), upload and attach to this policy
      if (pendingPdfFile && savedPolicy?.id) {
        try {
          const fd = new FormData();
          fd.append("file", pendingPdfFile, pendingPdfFile.name);
          const { data: updated } = await axios.post(`${API}/policies/${savedPolicy.id}/pdf`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setPolicies((x) => x.map((p) => (p.id === updated.id ? updated : p)));
        } catch (_e) { /* upload optional; do not block save */ }
      }
      setPendingPdfFile(null);
      setModal(null);
      setEditingPolicy(null); setPresetPolicyCustomer("");
    } catch (e) {
      window.alert("Poliçe kaydedilemedi. Sunucu yanıt vermiyor.");
    }
  };

  const uploadPdfToExistingPolicy = async (policyId, file, label = "") => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    const qs = label ? `?label=${encodeURIComponent(label)}` : "";
    const { data: updated } = await axios.post(`${API}/policies/${policyId}/pdf${qs}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setPolicies((x) => x.map((p) => (p.id === updated.id ? updated : p)));
    return updated;
  };

  const removePdfFromPolicy = async (policyId, attId = null) => {
    const url = attId ? `${API}/policies/${policyId}/pdf/${attId}` : `${API}/policies/${policyId}/pdf`;
    const { data: updated } = await axios.delete(url);
    setPolicies((x) => x.map((p) => (p.id === updated.id ? updated : p)));
    return updated;
  };

  const updatePdfLabel = async (policyId, attId, label) => {
    const { data: updated } = await axios.patch(`${API}/policies/${policyId}/pdf/${attId}`, { label });
    setPolicies((x) => x.map((p) => (p.id === updated.id ? updated : p)));
    return updated;
  };

  // State for in-page PDF preview modal (iframe).
  const [pdfPreview, setPdfPreview] = useState(null); // { policyId, attId?, filename, label? }
  const closeAllModals = () => {
    setModal(null); setSelectedCustomer(null); setPdfImport(false); setExcelImport(false);
    setChangePasswordOpen(false); setChangeEmailOpen(false); setPdfPreview(null); setConvertingQuote(null);
    setOnlineLinkOpen(false); setSearchOpen(false); setEmailingQuote(null); setPortalCustomer(null);
  };

  const openPolicyPdf = (policyId, attId = null) => {
    // Find info for header; fallback graceful if unknown.
    const p = policies.find((x) => x.id === policyId);
    let filename = p?.pdfFilename || "Poliçe PDF";
    let label = "";
    if (attId && Array.isArray(p?.pdfs)) {
      const hit = p.pdfs.find((a) => a.id === attId);
      if (hit) { filename = hit.filename || filename; label = hit.label || ""; }
    } else if (Array.isArray(p?.pdfs) && p.pdfs.length > 0) {
      filename = p.pdfs[0].filename || filename;
      label = p.pdfs[0].label || "";
    }
    setPdfPreview({ policyId, attId, filename, label });
  };

  const saveVehicle = async (item) => {
    try {
      if (item.id) {
        const { id, ...body } = item;
        const { data } = await axios.put(`${API}/vehicles/${id}`, body);
        setVehicles((x) => x.map((v) => (v.id === id ? data : v)));
      } else {
        const { id: _drop, ...body } = item;
        const { data } = await axios.post(`${API}/vehicles`, body);
        setVehicles((x) => [data, ...x]);
      }
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Kaydedilemedi";
      window.alert(`Araç kaydedilemedi: ${detail}`);
      return false;
    }
  };

  const deleteVehicle = async (vid) => {
    if (!window.confirm("Bu araç kaydını silmek istediğinize emin misiniz? (Poliçeler etkilenmez)")) return;
    try {
      await axios.delete(`${API}/vehicles/${vid}`);
      setVehicles((x) => x.filter((v) => v.id !== vid));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const bulkImportPolicies = async (items) => {
    const { data } = await axios.post(`${API}/policies/bulk`, { items });
    // Refresh policies & customers from server to include new records
    const [pol, cus] = await Promise.all([axios.get(`${API}/policies`), axios.get(`${API}/customers`)]);
    setPolicies(pol.data);
    setCustomers(cus.data);
    return data;
  };

  const toggleFavorite = async (customer) => {
    const updated = { ...customer, favorite: !customer.favorite };
    const { data } = await axios.put(`${API}/customers/${customer.id}`, updated);
    setCustomers((x) => x.map((c) => (c.id === data.id ? data : c)));
    setSelectedCustomer((prev) => (prev && prev.id === data.id ? data : prev));
  };

  const markQuotesSent = (ids) => setQuotes((x) => x.map((q) => (ids.includes(q.id) && q.status === "Taslak" ? { ...q, status: "Gönderildi" } : q)));

  const applyCustomerUpdate = (data) => {
    const old = customers.find((c) => c.id === data.id);
    setCustomers((x) => x.map((c) => (c.id === data.id ? data : c)));
    if (old && old.name !== data.name) {
      setPolicies((x) => x.map((p) => (p.customer === old.name ? { ...p, customer: data.name } : p)));
      setVehicles((x) => x.map((v) => (v.customer === old.name ? { ...v, customer: data.name } : v)));
      setQuotes((x) => x.map((q) => (q.customerId === data.id || (!q.customerId && q.customerName === old.name) ? { ...q, customerName: data.name } : q)));
    }
  };

  const saveCustomer = async (item) => {
    try {
      if (item.id) {
        // Update existing
        const { id, ...body } = item;
        const { data } = await axios.put(`${API}/customers/${id}`, body);
        applyCustomerUpdate(data);
      } else {
        const { id: _drop, ...body } = item;
        const { data } = await axios.post(`${API}/customers`, body);
        setCustomers((x) => [data, ...x]);
      }
      setModal(null);
      setEditingCustomer(null);
    } catch (e) {
      window.alert("Müşteri kaydedilemedi.");
    }
  };

  const saveFollowUp = async (item) => {
    try {
      const { id: _drop, ...body } = item;
      const { data } = await axios.post(`${API}/followups`, body);
      setFollowUps((x) => [data, ...x]);
      setModal(null);
      setFollowUpPolicy(null);
    } catch (e) {
      window.alert("Takip notu kaydedilemedi.");
    }
  };

  const addBranch = async (name, commissionRate = 15) => {
    try {
      const { data } = await axios.post(`${API}/branches`, { name, commissionRate: Number(commissionRate) });
      setBranches((x) => [...x, data]);
    } catch (e) {
      window.alert("Branş eklenemedi.");
    }
  };

  const updateBranch = async (id, patch) => {
    const current = branches.find((b) => b.id === id);
    if (!current) return;
    const next = { ...current, ...patch, commissionRate: Number(patch.commissionRate ?? current.commissionRate) };
    try {
      const { data } = await axios.put(`${API}/branches/${id}`, next);
      setBranches((x) => x.map((b) => (b.id === id ? data : b)));
    } catch (e) {
      window.alert("Branş güncellenemedi.");
    }
  };

  const saveSettings = async (s) => {
    try {
      const body = { netDivisor: Number(s.netDivisor), personalShare: Number(s.personalShare) };
      const { data } = await axios.put(`${API}/settings`, body);
      setSettings(data);
      setModal(null);
      return true;
    } catch (e) {
      window.alert("Ayarlar kaydedilemedi.");
      return false;
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm(type === "branch" ? "Bu kaydı silmek istediğinize emin misiniz?" : "Bu kayıt çöp kutusuna taşınacak (30 gün içinde geri yükleyebilirsiniz). Devam edilsin mi?")) return;
    try {
      const url = type === "policy" ? `policies/${id}` : type === "customer" ? `customers/${id}` : `branches/${id}`;
      const { data: del } = await axios.delete(`${API}/${url}`);
      if (type === "policy") {
        setPolicies((x) => x.filter((i) => i.id !== id));
        undoToast("Poliçe çöp kutusuna taşındı", async () => { const { data } = await axios.post(`${API}/policies/${id}/restore`); setPolicies((x) => [data, ...x.filter((p) => p.id !== id)]); setTrashPolicies((x) => x.filter((p) => p.id !== id)); });
      } else if (type === "customer") {
        setCustomers((x) => x.filter((i) => i.id !== id));
        if (del?.trashId) undoToast("Müşteri çöp kutusuna taşındı", async () => { const it = await restoreFromTrash(del.trashId); setCustomers((x) => [it, ...x]); });
      }
      else if (type === "branch") setBranches((x) => x.filter((i) => i.id !== id));
    } catch (e) {
      window.alert("Silme başarısız.");
    }
  };

  // --- Trash (Soft delete) handlers ---
  const loadTrash = async () => {
    try {
      const { data } = await axios.get(`${API}/policies/trash`);
      setTrashPolicies(data || []);
    } catch (e) { setTrashPolicies([]); }
  };

  const restorePolicy = async (id) => {
    try {
      const { data } = await axios.post(`${API}/policies/${id}/restore`);
      setTrashPolicies((x) => x.filter((p) => p.id !== id));
      setPolicies((x) => [data, ...x.filter((p) => p.id !== id)]);
    } catch (e) { window.alert("Geri yükleme başarısız."); }
  };

  const permanentDeletePolicy = async (id) => {
    if (!window.confirm("Bu poliçe KALICI olarak silinecek ve geri alınamaz. Emin misiniz?")) return;
    try {
      await axios.delete(`${API}/policies/${id}/permanent`);
      setTrashPolicies((x) => x.filter((p) => p.id !== id));
    } catch (e) { window.alert("Kalıcı silme başarısız."); }
  };

  // --- Quote handlers ---
  const saveQuote = async (input, pendingPdfFile = null) => {
    if (Array.isArray(input)) {
      try {
        const created = [];
        for (const it of input) {
          const { id: _d, ...body } = { ...it, premium: Number(it.premium) || 0, netPremium: it.netPremium === "" || it.netPremium == null ? null : Number(it.netPremium), commissionRate: Number(it.commissionRate) || 15 };
          created.push((await axios.post(`${API}/quotes`, body)).data);
        }
        if (pendingPdfFile && created[0]) {
          const fd = new FormData(); fd.append("file", pendingPdfFile);
          created[0] = (await axios.post(`${API}/quotes/${created[0].id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" }, params: { label: "Teklif PDF" } })).data;
        }
        setQuotes((x) => [...created, ...x]);
        setModal(null); setEditingQuote(null);
      } catch (e) { window.alert("Teklifler kaydedilemedi."); }
      return;
    }
    const item = input;
    const normalized = {
      ...item,
      premium: Number(item.premium) || 0,
      netPremium: (item.netPremium === "" || item.netPremium === null || item.netPremium === undefined) ? null : Number(item.netPremium),
      commissionRate: Number(item.commissionRate) || 15,
    };
    try {
      let saved;
      if (item.id) {
        const { data } = await axios.put(`${API}/quotes/${item.id}`, normalized);
        saved = data;
        setQuotes((x) => x.map((q) => (q.id === item.id ? data : q)));
      } else {
        const { id: _d, ...body } = normalized;
        const { data } = await axios.post(`${API}/quotes`, body);
        saved = data;
        setQuotes((x) => [data, ...x]);
      }
      // Attach pending PDF if provided (from PDF-import flow)
      if (pendingPdfFile && saved?.id) {
        try {
          const fd = new FormData(); fd.append("file", pendingPdfFile);
          const { data: withPdf } = await axios.post(`${API}/quotes/${saved.id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" }, params: { label: "Teklif PDF" } });
          setQuotes((x) => x.map((q) => (q.id === saved.id ? withPdf : q)));
        } catch (e) {
          window.alert("Teklif kaydedildi ancak PDF yüklenemedi: " + (e?.response?.data?.detail || e?.message || ""));
        }
      }
      setModal(null);
      setEditingQuote(null);
    } catch (e) { window.alert("Teklif kaydedilemedi."); }
  };

  const uploadPdfToQuote = async (qid, file, label = "") => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await axios.post(`${API}/quotes/${qid}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" }, params: { label } });
    setQuotes((x) => x.map((q) => (q.id === qid ? data : q)));
    return data;
  };

  const removePdfFromQuote = async (qid, attId) => {
    if (!window.confirm("Bu PDF'i silmek istediğinize emin misiniz?")) return null;
    try {
      const { data } = await axios.delete(`${API}/quotes/${qid}/pdf/${attId}`);
      setQuotes((x) => x.map((q) => (q.id === qid ? data : q)));
      return data;
    } catch (e) {
      window.alert("Silinemedi: " + (e?.response?.data?.detail || ""));
      return null;
    }
  };

  const openQuotePdf = (qid, att) => {
    const t = typeof window !== "undefined" ? (localStorage.getItem("sigortapro-token") || "") : "";
    const url = `${API}/quotes/${qid}/pdf?att=${encodeURIComponent(att?.id || "")}&token=${encodeURIComponent(t)}`;
    setPdfPreview({ url, filename: att?.filename || "teklif.pdf", label: att?.label || "" });
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Bu teklif çöp kutusuna taşınacak (30 gün içinde geri yükleyebilirsiniz). Devam edilsin mi?")) return;
    try {
      const { data: del } = await axios.delete(`${API}/quotes/${id}`);
      setQuotes((x) => x.filter((q) => q.id !== id));
      if (del?.trashId) undoToast("Teklif çöp kutusuna taşındı", async () => { const it = await restoreFromTrash(del.trashId); setQuotes((x) => [it, ...x]); });
    } catch (e) { window.alert("Silme başarısız."); }
  };

  const convertQuoteToPolicy = (quote) => {
    // Opens the ConvertQuoteModal to collect policeNo/start/end in one UX-friendly step
    setConvertingQuote(quote);
  };

  const submitConvertQuote = async ({ policeNo, start, end }) => {
    if (!convertingQuote) return;
    try {
      const { data: newPolicy } = await axios.post(`${API}/quotes/${convertingQuote.id}/convert`, {
        policeNo: policeNo || "",
        start: start || "",
        end: end || "",
      });
      setPolicies((x) => [newPolicy, ...x]);
      setQuotes((x) => x.map((q) => (q.id === convertingQuote.id ? { ...q, status: "Dönüştürüldü", convertedPolicyId: newPolicy.id } : q)));
      try {
        const { data: cus } = await axios.get(`${API}/customers`);
        setCustomers(cus);
      } catch {}
      setConvertingQuote(null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Dönüştürme başarısız." };
    }
  };

  const quickAdd = (k) => {
    if (k === "customer") { setEditingCustomer(null); setModal("customer"); }
    else if (k === "lead") { setEditingLead(null); setModal("lead"); }
    else if (k === "appointment") { setEditingAppt(null); setModal("appointment"); }
    else if (k === "quote") { setEditingQuote(null); setModal("quote"); }
    else { setEditingPolicy(null); setPresetPolicyCustomer(""); setModal("policy"); }
  };

  const fetchPublicQuoteSlug = async () => {
    try {
      const { data } = await axios.get(`${API}/me/public-quote-link`);
      setPublicQuoteSlug(data.slug || "");
    } catch (e) {}
  };

  const regenerateSlug = async () => {
    if (!window.confirm("Linki yenilerseniz eski link çalışmaz. Devam?")) return;
    try {
      const { data } = await axios.post(`${API}/me/public-quote-link/regenerate`);
      setPublicQuoteSlug(data.slug || "");
    } catch (e) { window.alert("Yenileme başarısız."); }
  };

  const exportCsv = () => {
    const rows = [
      ["Poliçe No", "Müşteri", "Branş", "Şirket", "Plaka", "Prim", "Branş Oranı %", "Acente Komisyon", "Kişisel Hak Ediş"],
      ...policies.map((p) => {
        const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null);
        return [p.policeNo, p.customer, p.branch, p.company, p.plate || "", p.premium, c.rate, c.agency.toFixed(2), c.personal.toFixed(2)];
      }),
    ];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sigortacrm-uretim.csv";
    a.click();
  };

  const exportExcel = (filter) => {
    // filter: { year?: number|null, month?: number|null, branch?: string|null }
    const fYear = filter && Number.isFinite(filter.year) ? filter.year : null;
    const fMonth = filter && Number.isFinite(filter.month) ? filter.month : null;
    const fBranch = filter && filter.branch ? filter.branch : null;
    const inRange = (dateStr) => {
      if (!dateStr) return fYear == null && fMonth == null;
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return false;
      if (fYear != null && d.getFullYear() !== fYear) return false;
      if (fMonth != null && d.getMonth() + 1 !== fMonth) return false;
      return true;
    };
    const noFilter = fYear == null && fMonth == null && fBranch == null;
    const filteredPolicies = noFilter
      ? policies
      : policies.filter((p) => {
          if ((fYear != null || fMonth != null) && !inRange(p.start)) return false;
          if (fBranch != null && p.branch !== fBranch) return false;
          return true;
        });

    const wb = XLSX.utils.book_new();
    const polSheet = filteredPolicies.map((p) => {
      const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null);
      return {
        "Poliçe No": p.policeNo,
        "Müşteri": p.customer,
        "Branş": p.branch,
        "Şirket": p.company,
        "Plaka": p.plate || "",
        "Başlangıç": p.start,
        "Bitiş": p.end,
        "Brüt Prim": p.premium,
        "Net Prim": Number(c.net.toFixed(2)),
        "Branş %": c.rate,
        "Acente Komisyon": Number(c.agency.toFixed(2)),
        "Kişisel Hak Ediş": Number(c.personal.toFixed(2)),
        "Ödeme": p.payment,
      };
    });
    // Customers included only when no filter, or those linked to filtered policies
    const filteredCustNames = new Set(filteredPolicies.map((p) => p.customer).filter(Boolean));
    const custRows = noFilter ? customers : customers.filter((c) => filteredCustNames.has(c.name));
    const custSheet = custRows.map((c) => ({
      "Ad / Ünvan": c.name,
      "Tür": c.type,
      "Telefon": c.phone,
      "Eposta": c.email,
      "T.C. / Vergi No": c.taxNo,
      "Adres": c.address,
      "Komisyon Verilen": c.commissionPerson,
      "Komisyon %": c.commissionRate,
    }));
    const renewSheet = upcoming
      .filter((p) => {
        if (fBranch != null && p.branch !== fBranch) return false;
        if (fYear == null && fMonth == null) return true;
        return inRange(p.end);
      })
      .map((p) => ({
        "Poliçe No": p.policeNo,
        "Müşteri": p.customer,
        "Branş": p.branch,
        "Plaka": p.plate || "",
        "Bitiş": p.end,
        "Kalan Gün": Math.max(0, daysLeft(p.end)),
        "Takip Durumu": followUps.some((f) => f.policyId === p.id) ? "Takip kaydı var" : "Takip bekliyor",
      }));
    const reportYear = fYear ?? YEAR;
    const monthly = MONTHS_FULL
      .map((m, idx) => {
        if (fMonth != null && idx + 1 !== fMonth) return null;
        const rows = policies.filter((p) => {
          if (fBranch != null && p.branch !== fBranch) return false;
          return p.start && new Date(p.start).getMonth() === idx && new Date(p.start).getFullYear() === reportYear;
        });
        const brut = rows.reduce((s, p) => s + Number(p.premium || 0), 0);
        const personal = rows.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
        return { "Ay": m, "Poliçe Sayısı": rows.length, "Brüt Prim": brut, "Kişisel Hak Ediş": Number(personal.toFixed(2)) };
      })
      .filter(Boolean);
    // Company breakdown sheet
    const companyMap = {};
    filteredPolicies.forEach((p) => {
      const key = (p.company || "(Belirtilmemiş)").trim() || "(Belirtilmemiş)";
      if (!companyMap[key]) companyMap[key] = { count: 0, brut: 0, personal: 0 };
      companyMap[key].count += 1;
      companyMap[key].brut += Number(p.premium || 0);
      companyMap[key].personal += calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal;
    });
    const companySheet = Object.entries(companyMap)
      .sort((a, b) => b[1].brut - a[1].brut)
      .map(([company, stats]) => ({
        "Şirket": company,
        "Poliçe Sayısı": stats.count,
        "Brüt Prim": Number(stats.brut.toFixed(2)),
        "Kişisel Hak Ediş": Number(stats.personal.toFixed(2)),
      }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(polSheet), "Poliçeler");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(custSheet), "Müşteriler");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(renewSheet), "Yenilemeler");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(monthly), "Aylık Üretim");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(companySheet), "Şirket Bazında");
    const parts = ["sigortacrm-yedek", String(reportYear)];
    if (fMonth != null) parts.push(String(fMonth).padStart(2, "0"));
    if (fBranch != null) parts.push(fBranch.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    XLSX.writeFile(wb, `${parts.join("-")}.xlsx`);
  };

  const importPdf = async (file, useOcr, onProgress) => {
    if (!file) throw new Error("Dosya seçilmedi");
    if (!/\.pdf$/i.test(file.name)) throw new Error("Dosya PDF olmalı (.pdf uzantısı yok)");
    let text = "";
    let pdf = null;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      pdf = await pdfjsLib.getDocument({
        data: bytes,
        useSystemFonts: true,
        isEvalSupported: false,
        cMapUrl: "https://unpkg.com/pdfjs-dist@4.10.38/cmaps/",
        cMapPacked: true,
        standardFontDataUrl: "https://unpkg.com/pdfjs-dist@4.10.38/standard_fonts/",
      }).promise;
      for (let i = 1; i <= pdf.numPages; i++) {
        const p = await pdf.getPage(i);
        const content = await p.getTextContent();
        let lastY = null;
        for (const it of content.items) {
          if (!it || typeof it.str !== "string") continue;
          const y = it.transform ? it.transform[5] : null;
          if (it.hasEOL || (lastY !== null && y !== null && Math.abs(y - lastY) > 2)) {
            text += "\n";
          } else if (text && !text.endsWith(" ") && !text.endsWith("\n")) {
            text += " ";
          }
          text += it.str;
          lastY = y;
        }
        text += "\n";
      }
    } catch (e) {
      throw new Error(`PDF dosyası okunamadı: ${e?.message || e}. Farklı bir dosyayla deneyin veya alanları elle girin.`);
    }
    const digitalTextOk = text.trim().length >= 30;
    if (!digitalTextOk && useOcr) {
      onProgress?.(0);
      let worker;
      try {
        worker = await createWorker("tur", 1, { logger: (m) => onProgress?.(Math.round((m.progress || 0) * 100)) });
        text = "";
        for (let i = 1; i <= pdf.numPages; i++) {
          const p = await pdf.getPage(i);
          const viewport = p.getViewport({ scale: 1.5 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await p.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          const result = await worker.recognize(canvas);
          text += result.data.text + "\n";
        }
      } catch (e) {
        throw new Error(`OCR başarısız: ${e?.message || e}. PDF taranmış olabilir; alanları elle doldurabilirsiniz.`);
      } finally {
        if (worker) {
          try { await worker.terminate(); } catch { /* ignore */ }
        }
      }
    }
    if (!text.trim() && !useOcr) {
      throw new Error("PDF taranmış görünüyor. Lütfen 'Taranmış PDF'lerde yerel OCR kullan' seçeneğini işaretleyip tekrar deneyin.");
    }
    const parsed = parsePolicyText(text, branches, file.name);
    const rate = parsed.branch ? branchRateOf(parsed.branch, branches) : (branches[0]?.commissionRate ?? 15);
    return { ...parsed, commissionRate: String(rate) };
  };

  if (authLoading) {
    return <div className="loading-screen" data-testid="loading-screen"><div className="loading-mark">SigortaCrm yükleniyor…</div></div>;
  }
  // Public quote form route (no auth required): /q/:slug
  const pubMatch = typeof window !== "undefined" ? window.location.pathname.match(/^\/q\/([a-z0-9]+)\/?$/i) : null;
  if (pubMatch) {
    return <PublicQuoteForm slug={pubMatch[1]} />;
  }
  // Public customer portal route (no auth required): /portal/:token
  const choiceMatch = typeof window !== "undefined" ? window.location.pathname.match(/^\/teklif-sec\/([\w-]+)\/?$/) : null;
  if (choiceMatch) {
    return <CompareChoicePage token={choiceMatch[1]} />;
  }
  const portalMatch = typeof window !== "undefined" ? window.location.pathname.match(/^\/portal\/([a-z0-9]+)\/?$/i) : null;
  if (portalMatch) {
    return <CustomerPortal token={portalMatch[1]} />;
  }
  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  if (resetToken) {
    return <ResetPasswordPage token={resetToken} />;
  }
  if (!user) {
    return <AuthPage />;
  }
  if (loading) {
    return <div className="loading-screen" data-testid="loading-screen"><div className="loading-mark">Portföyünüz getiriliyor…</div></div>;
  }

  const firstName = user.name.split(" ")[0] || user.email;

  return (
    <div className="app-shell">
      <Toaster position="bottom-center" richColors closeButton toastOptions={{ className: "app-toast" }} />
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark brand-logo" data-testid="brand-logo"><img src="/brand/logo-mark.png" alt="Sigorta CRM" /></div>
          <div>
            <strong>Sigorta<span>Crm</span></strong>
            <small>BİREYSEL TAKİP</small>
          </div>
          <button className="mobile-close" data-testid="sidebar-close-button" onClick={() => document.body.classList.remove("menu-open")}><X size={18} /></button>
        </div>
        <div className="workspace"><Building2 size={16} /><span>Benim Portföyüm</span><ChevronDown size={14} /></div>
        <nav>
          {nav.map(([id, label, Icon]) => {
            const inspectionCount = (vehicles || []).filter((v) => v.inspectionEnd && daysLeft(v.inspectionEnd) <= 60).length;
            let badge = 0;
            let badgeTestId = "";
            if (id === "renewals") { badge = upcoming.length + inspectionCount; badgeTestId = "nav-renewals-badge"; }
            if (id === "appointments") { badge = appointments.filter((a) => a.date === new Date().toLocaleDateString("sv-SE") && !a.result).length; badgeTestId = "nav-appointments-badge"; }
            return (
              <button key={id} className={page === id ? "active" : ""} onClick={() => navigate(id)} data-testid={`nav-${id}`}>
                <Icon size={18} /><span>{label}</span>
                {badge > 0 && <b data-testid={badgeTestId}>{badge}</b>}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <div className="sync-dot"><span></span>{apiStatus === "online" ? "Sistem bağlı" : "Bağlantı yok"}</div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="menu-button" data-testid="sidebar-menu-button" onClick={() => document.body.classList.add("menu-open")}>
            <Menu size={20} />
            {(() => {
              const inspectionCount = (vehicles || []).filter((v) => v.inspectionEnd && daysLeft(v.inspectionEnd) <= 60).length;
              const totalBadge = upcoming.length + inspectionCount;
              return totalBadge > 0 ? <b className="menu-badge" data-testid="mobile-menu-badge">{totalBadge}</b> : null;
            })()}
          </button>
          <button type="button" className="top-search" data-testid="open-global-search" onClick={() => setSearchOpen(true)} title="Hızlı arama (Ctrl+K)">
            <Search size={17} />
            <span className="top-search-placeholder">Hızlı arama: poliçe, müşteri, teklif, plaka...</span>
            <kbd className="kbd-hint">⌘K</kbd>
          </button>
          <div className="top-actions">
            <ThemeToggle />
            <NotificationsBell onNavigate={(url) => { const sp = new URLSearchParams((url || "").split("?")[1] || ""); if (sp.get("page")) { setQuoteFilter(sp.get("filter") || "all"); setPage(sp.get("page")); } if (sp.get("check")) setCheckQuoteId(sp.get("check")); }} />
            <div className="profile" data-testid="profile-menu-trigger" onClick={() => setProfileMenuOpen((v) => !v)}>
              <UserAvatar user={user} testId="profile-avatar" />
              <div className="profile-info">
                <strong data-testid="profile-name">{user.name}</strong>
                <small>{user.email}</small>
              </div>
              <ChevronDown size={15} />
              {profileMenuOpen && (
                <div className="profile-menu" data-testid="profile-menu">
                  <button data-testid="change-password-button" onClick={(e) => { e.stopPropagation(); setChangePasswordOpen(true); setProfileMenuOpen(false); }}><KeyRound size={15} /> Şifre değiştir</button>
                  <button data-testid="change-email-button" onClick={(e) => { e.stopPropagation(); setChangeEmailOpen(true); setProfileMenuOpen(false); }}><Mail size={15} /> E-posta değiştir</button>
                  <button data-testid="online-quote-link-button" onClick={(e) => { e.stopPropagation(); setOnlineLinkOpen(true); fetchPublicQuoteSlug(); setProfileMenuOpen(false); }}><ExternalLink size={15} /> Online teklif linkim</button>
                  <button data-testid="logout-button" onClick={(e) => { e.stopPropagation(); logout(); }}><LogOut size={15} /> Çıkış yap</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="content">
          <InstallPrompt />
          <PageErrorBoundary key={page}>
          {page === "dashboard" && <PendingChoices quotes={quotes} onOpen={setCheckQuoteId} />}
          {page === "dashboard" && <Dashboard policies={policies} customers={customers} branches={branches} settings={settings} followUps={followUps} upcoming={upcoming} totalPremium={totalPremium} totalCommissionPersonal={totalCommissionPersonal} vehicles={vehicles} quotes={quotes} onOpenRequests={() => { setQuoteFilter("requests"); setPage("quotes"); }} onPrepareRequest={(q) => { setEditingQuote(q); setModal("quote"); }} setPage={setPage} setModal={setModal} onOpenCustomer={setSelectedCustomer} onOpenPolicy={(p) => { setEditingPolicy(p); setModal("policy"); }} onPolicyUpdated={(np) => setPolicies((list) => list.map((x) => (x.id === np.id ? np : x)))} />}
          {page === "policies" && <Policies policies={policies} customers={customers} branches={branches} settings={settings} query={query} setQuery={setQuery} setModal={setModal} setPdfImport={setPdfImport} setExcelImport={setExcelImport} setEditingPolicy={setEditingPolicy} remove={remove} exportCsv={exportCsv} exportExcel={exportExcel} openPolicyPdf={openPolicyPdf} can={can} />}
          {page === "customers" && <Customers onQuotesSent={markQuotesSent} customers={customers} policies={policies} quotes={quotes} vehicles={vehicles} onCustomerUpdated={applyCustomerUpdate} onVehicleSaved={(v) => setVehicles((list) => (list.some((x) => x.id === v.id) ? list.map((x) => (x.id === v.id ? v : x)) : [v, ...list]))} branches={branches} settings={settings} query={query} setQuery={setQuery} setModal={setModal} setSelectedCustomer={setSelectedCustomer} remove={remove} setEditingCustomer={setEditingCustomer} setEditingPolicy={setEditingPolicy} setEditingQuote={setEditingQuote} onNewPolicyFor={(c) => { setEditingPolicy(null); setPresetPolicyCustomer(c.name); setModal("policy"); }} onNewQuoteFor={(c) => { setEditingQuote({ customerId: c.id, customerName: c.name, customerPhone: c.phone || "", customerEmail: c.email || "" }); setModal("quote"); }} onToggleFavorite={toggleFavorite} onOpenPortal={setPortalCustomer} can={can} />}
          {page === "leads" && <Leads leads={leads} appointments={appointments} quotes={quotes} onNewQuote={(l) => { setEditingQuote({ customerId: l.id, customerName: l.name, customerPhone: l.phone || "", customerEmail: l.email || "" }); setModal("quote"); }} onNew={() => quickAdd("lead")} onEdit={(l) => { setEditingLead(l); setModal("lead"); }} onConvert={convertLead} onDelete={deleteLead} onNewAppointment={(l) => { setEditingAppt({ customerId: l.id, customerKind: "lead", customerName: l.name, phone: l.phone }); setModal("appointment"); }} />}
          {page === "appointments" && <Appointments appointments={appointments} customers={customers} leads={leads} onNew={() => quickAdd("appointment")} onEdit={(a) => { setEditingAppt(a); setModal("appointment"); }} onUpdated={upsertAppt} onDeleted={(id, tid) => { setAppointments((x) => x.filter((a) => a.id !== id)); if (tid) undoToast("Randevu çöp kutusuna taşındı", async () => upsertAppt(await restoreFromTrash(tid))); }} />}
          {page === "vehicles" && <Vehicles policies={policies} customers={customers} branches={branches} settings={settings} vehicles={vehicles} query={query} setQuery={setQuery} setEditingPolicy={setEditingPolicy} setEditingVehicle={setEditingVehicle} setModal={setModal} setFollowUpPolicy={setFollowUpPolicy} deleteVehicle={deleteVehicle} />}
          {page === "renewals" && <Renewals upcoming={upcoming} followUps={followUps} setFollowUpPolicy={setFollowUpPolicy} setModal={setModal} customers={customers} userName={user?.name || ""} vehicles={vehicles} setEditingVehicle={setEditingVehicle} />}
          {page === "commission" && <Commission policies={policies} customers={customers} branches={branches} settings={settings} />}
          {page === "reports" && can("canViewReports") && <Reports quotes={quotes} onPolicyUpdated={(np) => setPolicies((list) => list.map((x) => (x.id === np.id ? np : x)))} policies={policies} customers={customers} branches={branches} settings={settings} exportCsv={exportCsv} exportExcel={exportExcel} can={can} />}
          {page === "quotes" && <QuotesPage key={quoteFilter} initialFilter={quoteFilter} quotes={quotes} customers={customers} branches={branches} userName={user?.name || ""} setEditingQuote={(q) => { setEditingQuote(q); setModal("quote"); }} onDelete={deleteQuote} onConvert={convertQuoteToPolicy} onOpenOnlineLink={() => setOnlineLinkOpen(true)} onOpenPdf={openQuotePdf} onEmail={setEmailingQuote} onPdfSent={markQuotesSent} onCheck={setCheckQuoteId} />}
          {page === "preferences" && <SettingsPage user={user} onUserUpdated={refreshUser} onEditCustomer={(id) => { const c = customers.find((x) => x.id === id); if (c) { setEditingCustomer(c); setModal("customer"); } }} settings={settings} onSaveSettings={saveSettings} canEditSettings={can("canEditSettings")} onChangePassword={() => setChangePasswordOpen(true)} onChangeEmail={() => setChangeEmailOpen(true)} />}
          {(page === "branches" || page === "preferences") && can("canEditBranches") && <section className="settings-embed" id="settings-branches" data-testid="settings-branches-section"><Branches calcCard={can("canEditSettings") && <CalcSettingsCard settings={settings} onSave={saveSettings} />} branches={branches} policies={policies} addBranch={addBranch} updateBranch={updateBranch} remove={remove} setModal={setModal} settings={settings} /></section>}
          {page === "trash" && <TrashPage trashPolicies={trashPolicies} loadTrash={loadTrash} onRestore={restorePolicy} onPermanentDelete={permanentDeletePolicy} onItemRestored={(k, it) => { if (k === "customer") setCustomers((x) => [it, ...x]); if (k === "quote") setQuotes((x) => [it, ...x]); }} />}
          {page === "admin" && isAdmin && <AdminPanel />}
          {page === "reports" && !can("canViewReports") && <Empty text="Raporlar sayfasına erişim yetkiniz yok" />}
          {page === "branches" && !can("canEditBranches") && <Empty text="Branşlar sayfasına erişim yetkiniz yok" />}
          {page === "admin" && !isAdmin && <Empty text="Bu sayfaya yalnızca yöneticiler erişebilir" />}
          </PageErrorBoundary>
        </div>
      </main>

      <PageErrorBoundary overlay title="Pencere açılırken bir sorun oluştu" onDismiss={closeAllModals}>
      {modal === "policy" && <PolicyModal branches={branches} customers={customers} settings={settings} initial={editingPolicy} presetCustomer={presetPolicyCustomer} onSave={savePolicy} onClose={() => { setModal(null); setEditingPolicy(null); setPendingPdfFile(null); setPresetPolicyCustomer(""); }} pendingPdfFile={pendingPdfFile} setPendingPdfFile={setPendingPdfFile} openPolicyPdf={openPolicyPdf} uploadPdfToExistingPolicy={uploadPdfToExistingPolicy} removePdfFromPolicy={removePdfFromPolicy} updatePdfLabel={updatePdfLabel} />}
      {checkQuoteId && quotes.find((q) => q.id === checkQuoteId) && <QuoteCheckModal quote={quotes.find((q) => q.id === checkQuoteId)} onClose={() => setCheckQuoteId(null)}
        onUpdated={(nq) => setQuotes((x) => x.map((q) => (q.id === nq.id ? nq : q)))} onEdit={(q) => { setEditingQuote(q); setModal("quote"); }} onConvert={convertQuoteToPolicy} />}
      {modal === "lead" && <LeadModal initial={editingLead} onSave={saveLead} onClose={() => { setModal(null); setEditingLead(null); }} />}
      {modal === "appointment" && <AppointmentModal initial={editingAppt} customers={customers} leads={leads} onLeadCreated={(l) => setLeads((x) => [l, ...x])} onSaved={(a) => { upsertAppt(a); setModal(null); setEditingAppt(null); }} onClose={() => { setModal(null); setEditingAppt(null); }} />}
      {modal === "customer" && <CustomerModal initial={editingCustomer} onSave={saveCustomer} onClose={() => { setModal(null); setEditingCustomer(null); }} />}
      {modal === "followup" && <FollowUpModal policy={followUpPolicy} onSave={saveFollowUp} onClose={() => { setModal(null); setFollowUpPolicy(null); }} />}
      {modal === "settings" && <SettingsModal settings={settings} onSave={saveSettings} onClose={() => setModal(null)} />}
      {selectedCustomer && <CustomerDetail customer={selectedCustomer} policies={policies} branches={branches} settings={settings} onEdit={() => { setEditingCustomer(selectedCustomer); setSelectedCustomer(null); setModal("customer"); }} onClose={() => setSelectedCustomer(null)} onToggleFavorite={toggleFavorite} onOpenPortal={setPortalCustomer} />}
      {pdfImport && <PdfImportModal parseFn={importPdf} onFinalize={(data, file) => { setPdfImport(false); setPendingPdfFile(file); setEditingPolicy(data); setModal("policy"); }} onClose={() => setPdfImport(false)} />}
      {excelImport && <ExcelImportModal branches={branches} onImport={bulkImportPolicies} onClose={() => setExcelImport(false)} />}
      {changePasswordOpen && <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />}
      {changeEmailOpen && <ChangeEmailModal currentEmail={user?.email} onClose={() => setChangeEmailOpen(false)} onSuccess={() => { if (refreshUser) refreshUser(); }} />}
      {pdfPreview && <PdfPreviewModal preview={pdfPreview} onClose={() => setPdfPreview(null)} />}
      {modal === "vehicle" && <VehicleModal initial={editingVehicle} customers={customers} onSave={async (item) => { const ok = await saveVehicle(item); if (ok) { setModal(null); setEditingVehicle(null); } }} onClose={() => { setModal(null); setEditingVehicle(null); }} />}
      {modal === "quote" && <QuoteModal initial={editingQuote} customers={customers} branches={branches} parsePdfFn={importPdf} onSave={saveQuote} onClose={() => { setModal(null); setEditingQuote(null); }} onUploadPdf={uploadPdfToQuote} onRemovePdf={removePdfFromQuote} onOpenPdf={openQuotePdf} />}
      {convertingQuote && <ConvertQuoteModal quote={convertingQuote} onSubmit={submitConvertQuote} onClose={() => setConvertingQuote(null)} />}
      {onlineLinkOpen && <OnlineQuoteLinkModal slug={publicQuoteSlug} onFetch={fetchPublicQuoteSlug} onRegenerate={regenerateSlug} onClose={() => setOnlineLinkOpen(false)} />}
      {searchOpen && <GlobalSearchModal policies={policies} customers={customers} quotes={quotes} vehicles={vehicles} setPage={setPage} onOpenCustomer={(c) => { setSelectedCustomer(c); setSearchOpen(false); }} onOpenPolicy={(p) => { setEditingPolicy(p); setModal("policy"); setSearchOpen(false); }} onOpenQuote={(q) => { setEditingQuote(q); setModal("quote"); setSearchOpen(false); }} onClose={() => setSearchOpen(false)} />}
      {emailingQuote && <QuoteEmailModal quote={emailingQuote} onClose={() => setEmailingQuote(null)} />}
      {portalCustomer && <CustomerPortalLinkModal customer={portalCustomer} onClose={() => setPortalCustomer(null)} />}
      </PageErrorBoundary>
      <QuickAddFab onPick={quickAdd} />
    </div>
  );
}

export default App;
